// v3 explain shape -- client-side mirror of api `explain-v3.types.ts`
// (which itself mirrors python `rebalance.explain_v3.ExplainResponseV3`).
// Matches s-explain-v3-architecture-019e03f7 §3. Field renames vs v2
// per §3.5.
//
// The python schema is the source of truth. This file is duplicated
// so the client tsc compile doesn't depend on the api project being
// built first (which composite-mode would require). When the schema
// changes, update python first, then api/, then this file.

export type CashTriggered = "min" | "max" | null;

export type ModelAssignmentStrategy =
  "fallback" | "preference" | "completion" | "soft_completion";

export type CheckpointKey =
  | "holdings"
  | "model"
  | "cash"
  | "restrictions"
  | "substitutions"
  | "constrain"
  | "final";

export type FindingSeverity = "critical" | "error" | "warning" | "info";

/** Origin of an audit finding: the deterministic engine vs an LLM agent. */
export type FindingSource = "deterministic" | "agent";

/**
 * Enumerated finding category — mirror of the api `audit.types.ts` shape
 * (s-rebalance-audit-analysis-pipeline-019eb94b D7). The reserved value
 * `spec_divergence` (SC3) marks a bug-hunt finding; the set is open to growth.
 */
export type FindingCategory =
  | "spec_divergence"
  | "budget_breach"
  | "ac_overshoot"
  | "constraint_violation"
  | "cash_drift"
  | "allocation_sum"
  | "wash_sale"
  | "explanation"
  | "other";

/**
 * The bug-hunt divergence assessment carried by a `category=spec_divergence`
 * finding (AC4 third clause).
 */
export interface DivergenceAssessment {
  status: "no_divergence" | "divergence_found";
  spec?: string;
  detail?: string;
}

/**
 * D7 findings contract (client mirror of api `audit.types.ts AuditFinding`).
 * EXTENDS the existing `ChecksPanel.Finding` shape with `checkpoint`, `source`,
 * `category`, and the generalized `asset_class?` entity reference. The python
 * schema is the source of truth; keep this in lockstep with the api mirror.
 */
export interface AuditFinding {
  code: string;
  severity: FindingSeverity;
  title: string;
  description: string;
  recommendation?: string;
  agent?: string;
  account_id?: number;
  symbol?: string;
  /** The panel this finding lands on (D5 step→checkpoint map). */
  checkpoint: CheckpointKey;
  source: FindingSource;
  category: FindingCategory;
  /** Generalized entity reference alongside `account_id` / `symbol` (R9). */
  asset_class?: string;
  /** Present iff `category === "spec_divergence"` (AC4 third clause). */
  divergence_found?: DivergenceAssessment;
}

/**
 * The staff audit endpoint payload (`/admin/rebalances/:id/audit` →
 * primed into the firm tree's `useAudit` cache). Carries the D7 `findings` for
 * the inline cards and the pointer `maxSeverity` for the list/header rollup.
 */
export interface RebalanceAuditResponse {
  rebalanceId: number;
  maxSeverity: FindingSeverity | null;
  findings: AuditFinding[];
}

export interface AllocationDriftRow {
  name: string;
  current: string;
  target: string;
  delta: string;
  breached: boolean;
}

export interface TriggerData {
  reason: string;
  reason_detail: string;
  hint: string;
  severity_pct: string | null;
  allocation_drift: AllocationDriftRow[];
}

export interface HouseholdModelInfo {
  id: number | null;
  name: string | null;
  primary: Record<string, string>;
  secondary: Record<string, string> | null;
}

export interface RequestData {
  as_of_date: string;
  household_id: number;
  rebalance_type: string;
  household_model: HouseholdModelInfo | null;
}

export interface PreferenceRule {
  account_id: number;
  model_id: number | null;
  model_name: string | null;
  source: string;
  explanation: string;
}

// Position constraint flags — the binding constraint that held a
// position above target during overshoot redistribution. Advisor
// remediation per s-soft-completion-019e31e8 §4. After soft-completion
// follow-up #1 the pass emits the specific binding constraint;
// `lot_selection_pinned` is retained for completeness but is no longer
// emitted by this pass.
export type BlockingPositionFlag =
  | "hit_gains_budget"
  | "hit_wash_sale"
  | "hit_hold_only"
  | "lot_selection_pinned";

// A position held above the soft-completion model target that blocked
// overshoot redistribution from reaching the household AC target.
export interface BlockingPositionData {
  account_id: number;
  dollars: string;
  flags: BlockingPositionFlag[];
}

// Per asset-class warning emitted when redistribution could not fully
// absorb the household overshoot — names the blocking positions.
export interface OvershootResidualWarningData {
  ac: string;
  residual_dollars: string;
  hh_target_dollars: string;
  blocking_positions: BlockingPositionData[];
}

// Per asset-class overshoot accounting: `detected` after the tax-aware
// pass, `absorbed` by scaling flexible positions down, `residual` the
// unabsorbed remainder (`detected - absorbed`).
export interface ACOvershootData {
  ac: string;
  detected: string;
  absorbed: string;
  residual: string;
}

// --- E4 enrichment (explains-page-contract.json): overshoot
// redistribution detail — where the overshoot came from and who
// absorbed it. All fields optional; absent on pre-enrichment payloads.

// Per asset-class post-fit demand (contract E4): per-account dollars
// nested under `by_account` — the emitter keeps account-id keys out of
// the object carrying `total` / `budget`.
export interface OvershootDemandEntry {
  by_account: Record<string, string>;
  total: string;
  budget: string;
}

// One account's contribution to an asset class pool: flexible
// positions can be trimmed; pinned positions carry the constraint
// flags that hold them in place.
export interface OvershootPoolPosition {
  account_id: number;
  dollars: string;
  flags?: BlockingPositionFlag[];
}

export interface OvershootPoolEntry {
  flexible: OvershootPoolPosition[];
  pinned: OvershootPoolPosition[];
}

// One scale-down applied to a flexible position during redistribution.
export interface OvershootTrimRow {
  account_id: number;
  ac: string;
  before: string;
  after: string;
  trimmed: string;
  factor: string;
}

// Where one account's freed weight was redeployed (tier 1 = asset
// class, 2 = risk, 3 = account model).
export interface OvershootRedeployTarget {
  tier: 1 | 2 | 3;
  target: string;
  dollars: string;
}

export interface OvershootRedeployEntry {
  account_id: number;
  freed_pct: string;
  into: OvershootRedeployTarget[];
}

// Soft-completion overshoot redistribution — the post-hoc pass that
// absorbs residual household asset-class overshoot after the tax-aware
// per-account pass (s-soft-completion-019e31e8 §3). Present only on
// SoftCompletionData; the field is `null` when the pass detected no
// household AC overshoot.
export interface OvershootRedistributionData {
  overshoot_by_ac: ACOvershootData[];
  warnings: OvershootResidualWarningData[];
  // E4 enrichment fields — absent on pre-enrichment payloads.
  demand_by_ac?: Record<string, OvershootDemandEntry>;
  pools?: Record<string, OvershootPoolEntry>;
  trim_rows?: OvershootTrimRow[];
  redeploy?: OvershootRedeployEntry[];
}

// --- Completion strategy payload (s-completion-strategy-explain-019e4107 §1) ---
// All numeric fields arrive as strings (python Decimal / float serialised
// as JSON strings); the panel parses them at the display edge.

// One account in the completion processing roster.
export interface CompletionAccountClassification {
  account_id: number;
  has_explicit_model: boolean;
  model_id: number | null;
  processing_order: number;
  processing_group: string;
  balance: string;
  processing_order_reason: string;
  // --- E2 enrichment (explains-page-contract.json): gains-at-risk
  // detail behind the taxable re-rank. All nullable — null (or absent
  // on pre-enrichment payloads) when the re-rank does not apply to
  // this account (tax-exempt or no own model).
  contested_ac?: string | null;
  held_dollars?: string | null;
  model_target_dollars?: string | null;
  gains_at_risk_dollars?: string | null;
}

// Constrained pool after processing one account: the cumulative
// immutable (model + hold_only) dollars and the residual remaining.
export interface ConstrainedPoolProgressionEntry {
  after_account_id: number;
  total_constrained_amount: string;
  by_asset_class: Record<string, string>;
  by_risk_level: Record<string, string>;
  by_symbol_count: number;
  residual_by_asset_class: Record<string, string>;
}

// risk-level waterfall row: Ideal / Constrained / Residual.
export interface ResidualRiskLevelRow {
  risk_level: string;
  ideal: string;
  constrained: string;
  residual: string;
}

// asset-class waterfall row: adds Waterfall Allocated to the risk-level
// shape. `residual` here is `waterfall_allocated - constrained`.
export interface ResidualACLevelRow {
  risk_level: string;
  asset_class: string;
  ideal: string;
  constrained: string;
  waterfall_allocated: string;
  residual: string;
}

// security-level mapping row: per-symbol Target$ / Constrained$ /
// Waterfall$ / Residual$.
export interface ResidualSecurityMappingRow {
  symbol: string;
  model_weight: string;
  asset_class: string;
  target_dollars: string;
  constrained_dollars: string;
  waterfall_dollars: string;
  residual: string;
}

// The three-table residual waterfall trace for one no-model account.
export interface ResidualCalculation {
  risk_level_rows: ResidualRiskLevelRow[];
  ac_level_rows: ResidualACLevelRow[];
  security_mapping_rows: ResidualSecurityMappingRow[];
}

// The computed residual model + waterfall trace for one no-model account.
export interface CompletionResidualModel {
  account_id: number;
  residual_target_weights: Record<string, string>;
  raw_residuals_by_asset_class: Record<string, string>;
  adjusted_residuals_by_asset_class: Record<string, string>;
  scaled: boolean;
  scale_factor: string | null;
  excluded_symbols: string[];
  residual_calculation: ResidualCalculation;
}

// One position whose constrained allocation diverged from target.
export interface CompletionConstraintImpact {
  account_id: number;
  symbol: string;
  target_weight: string;
  actual_weight: string;
  deviation: string;
  cause: string;
}

// Household-level verification of the assembled completion result.
// `passed === false` is expected, explanatory content — not an error.
export interface CompletionVerification {
  passed: boolean;
  max_deviation: string;
  deviations_by_asset_class: Record<string, string>;
  deviations_by_symbol: Record<string, string>;
  constraint_impacts: CompletionConstraintImpact[];
}

// Per-account framing for the panel's account-detail rows. The emitter
// also writes `constrained_model` / `budget_ratios`; they are carried
// for completeness but not rendered.
export interface CompletionAccountDetail {
  account_id: number;
  role: string;
  balance: string;
  pct_of_household: string;
  household_risk: Record<string, string>;
  constrained_risk: Record<string, string>;
  residual_risk: Record<string, string>;
  household_target_by_asset_class: Record<string, string>;
  constrained_by_asset_class: Record<string, string>;
  residual_by_asset_class: Record<string, string>;
  // symbol -> weight maps on own-model accounts; null on residual
  // accounts. (Previously mistyped `string | null` — the python
  // emitter has always written dicts; nothing rendered them before.)
  original_model: Record<string, string> | null;
  filtered_model: Record<string, string> | null;
  constrained_model?: Record<string, string> | null;
  budget_ratios?: Record<string, string> | null;
  // --- E1 enrichment: asset classes the household budget holds that
  // this account's model cannot reach. Absent pre-enrichment.
  unreachable_household_acs?: string[];
  // --- E3 enrichment: soft-cap outcome. `capped_to_pct + leftover_pct
  // == 1`; `after_cap_model` sums to `capped_to_pct`. Absent
  // pre-enrichment.
  capped_to_pct?: string;
  leftover_pct?: string;
  after_cap_model?: Record<string, string>;
}

// v3-native completion-strategy payload. The python emitter writes
// `model_assignment.completion` as this data object directly. Populated
// for `strategy` "completion" and "soft_completion"; `null` for
// "fallback" / "preference" (the APP-1238 structural fix).
export interface CompletionData {
  strategy: ModelAssignmentStrategy;
  household_aum: string;
  household_target_by_asset_class: Record<string, string>;
  account_classifications: CompletionAccountClassification[];
  processing_order: number[];
  constrained_pool_initial: Record<string, string>;
  constrained_pool_progression: ConstrainedPoolProgressionEntry[];
  residual_models: Record<string, CompletionResidualModel>;
  verification: CompletionVerification;
  summary: string[];
  account_details: Record<string, CompletionAccountDetail>;
}

// --- E7 enrichment: Step 1.5 no-model gains surfacing — per no-model
// taxable account, the gain-locked value that pre-claims pool budget
// before model fit. `provisional` marks the contribution as removed
// before stage 6 and re-processed in stage 7.
export interface NoModelGainsLock {
  by_symbol: Record<string, string>;
  by_asset_class: Record<string, string>;
  total: string;
  provisional: boolean;
}

// Soft-completion extends the completion strategy with the overshoot
// redistribution pass. Emitted as `model_assignment.completion` when
// `strategy === "soft_completion"`.
export interface SoftCompletionData extends CompletionData {
  overshoot_redistribution: OvershootRedistributionData | null;
  // --- E1 enrichment: symbols in the household model primary that
  // appear in no account model. Absent pre-enrichment.
  household_only_symbols?: string[];
  // --- E7 enrichment: account_id -> gain-locked detail. Absent
  // pre-enrichment.
  no_model_gains_locks?: Record<string, NoModelGainsLock>;
}

export interface ModelAssignmentData {
  strategy: ModelAssignmentStrategy;
  rules: PreferenceRule[];
  // v3-native completion payload: `CompletionData` for "completion",
  // `SoftCompletionData` for "soft_completion", `null` for fallback /
  // preference (the APP-1238 structural fix).
  completion: CompletionData | null;
}

export interface CashAdjustmentTarget {
  weight: string | null;
  dollar: string | null;
}

export interface CashAdjustmentBand {
  min: string | null;
  max: string | null;
}

/**
 * An intraday cash adjustment the run applied to the account
 * (`CashBalanceOverrideData`, `api/src/rebalances/explain-v3.types.ts`).
 */
export interface CashBalanceOverrideData {
  reported_cash_balance: string;
  cash_balance: string;
  delta: string;
  reason: string | null;
}

export interface CashAdjustmentData {
  target: CashAdjustmentTarget;
  band: CashAdjustmentBand;
  current_weight: string | null;
  triggered: CashTriggered;
  adjustment_pp: string;
  securities_scale: string;
  explanation: string;
}

export interface RestrictionEntry {
  symbol: string;
  type: string;
  locked_weight: string;
  source: string;
}

export interface RestrictionAdjustmentData {
  restrictions: RestrictionEntry[];
}

export interface SubstitutionEntry {
  primary: string;
  secondary: string;
  treatment: string;
  weight_transferred: string;
}

export interface SecondarySecuritiesData {
  substitutions: SubstitutionEntry[];
}

export interface TaxConstraintsData {
  tax_budget: string | null;
  available_tax_budget: string | null;
  gains_budget_st: string | null;
  gains_budget_lt: string | null;
  available_gains_budget_st: string | null;
  available_gains_budget_lt: string | null;
  st_tax_rate: string | null;
  lt_tax_rate: string | null;
  ytd_realized_gains_st: string;
  ytd_realized_gains_lt: string;
  lot_selection_method: string;
  budget_limited_sells: string[];
  explanation: string;
}

// Structured cause of a constraint adjustment / lot stop. Mirror of
// python `rebalance.explain_v2.StopReason`. Drives the v3
// ConstrainSection — `issue_*` reasons are caps that prevented
// reaching target, `by_design_*` are intentional restrictions.
// Per decision dc-constrain-explain-payload-stop-reasons-019e1dc7.
export type StopReason =
  | "fully_traded"
  | "tax_budget_cap"
  | "st_gains_cap"
  | "lt_gains_cap"
  | "no_shares_left"
  | "buy_side_dropped"
  | "round_lot_floor"
  | "restriction_locked"
  | "substituted"
  | "excluded"
  | "residual_redeploy";

export type RowClass = "overweight" | "underweight" | "at_target";

// Stop reasons that block reaching target (advisor "issue" surface).
export const ISSUE_STOP_REASONS: StopReason[] = [
  "tax_budget_cap",
  "st_gains_cap",
  "lt_gains_cap",
  "no_shares_left",
  "buy_side_dropped",
  "round_lot_floor",
];

// Stop reasons that are intentional (advisor "by design" surface).
export const BY_DESIGN_STOP_REASONS: StopReason[] = [
  "restriction_locked",
  "substituted",
  "excluded",
  "residual_redeploy",
];

export interface ConstraintAdjustmentRow {
  constraint_type: string;
  symbol: string;
  target_before: string;
  target_after: string;
  reason: string;
  // Optional for back-compat with payloads persisted before APP-1241.
  stop_reason?: StopReason | null;
  // Pre-cap target the algorithm aimed for before a cap clipped it.
  // Present on capped rows (issue bucket + round_lot_floor); absent on
  // non-capped rows where target_after is the intent. Lets the UI
  // render "intended X% → final Y%" with correct direction:
  //   target_after > intended_target → cap prevented further sell
  //   target_after < intended_target → cap prevented further buy
  intended_target?: string | null;
}

export interface LotAdjustmentRow {
  symbol: string;
  action: string;
  shares: string;
  realized_gain: string;
  limiting_factor: string | null;
  explanation: string;
  // HIFO sort key (cost_basis_per_share) or MTAX tax-efficiency ratio.
  // `null` on IRA lots — no walker engaged.
  priority?: string | null;
  stop_reason?: StopReason | null;
}

// Hierarchical waterfall node emitted by python
// `rebalance.waterfall.serialize_tree`. The outer list is one node
// per risk level (e.g. Growth / Defensive); each risk node contains
// asset-class children; each asset-class contains symbol leaves.
// `constrained` is the immovable floor from upstream restrictions
// (hold_only / sell_no_tax / exclude). `allocated` is what the
// waterfall produced.
export interface AllocationTreeNode {
  name: string;
  target: string;
  constrained: string;
  allocated: string;
  children?: AllocationTreeNode[];
}

export interface ConstrainedAllocationData {
  final_target: Record<string, string>;
  adjustments: ConstraintAdjustmentRow[];
  lot_adjustments: LotAdjustmentRow[];
  allocation_tree: AllocationTreeNode[] | null;
  // Per-symbol weight at the start of the constrain pass.
  current_weights?: Record<string, string>;
  // Per-symbol classification from sign(current - model).
  row_classes?: Record<string, RowClass>;
  // Realized gains rolled up across this account's lot trades.
  realized_st?: string;
  realized_lt?: string;
  // Per-StopReason counts of caps that prevented reaching target.
  issue_counts?: Record<string, number>;
  // Per-StopReason counts of intentional restrictions / substitutions /
  // exclusions / residual redeploys.
  by_design_counts?: Record<string, number>;
}

export interface TradeRow {
  symbol: string;
  side: "buy" | "sell" | "sell_all";
  shares: string;
  value: string;
  realized_gain_st: string;
  realized_gain_lt: string;
  reason: string;
}

export interface TradeGenerationData {
  trades: TradeRow[];
  buy_total: string;
  sell_total: string;
  realized_gain_st: string;
  realized_gain_lt: string;
  total_tax_impact: string | null;
}

export interface CheckpointData {
  by_symbol: Record<string, string>;
}

export interface AccountModelInfo {
  id: number | null;
  name: string | null;
  primary: Record<string, string>;
  secondary: Record<string, string> | null;
}

export interface AccountTrade {
  delta_dollar: string;
  shares_net: string;
  buy_total: string;
  sell_total: string;
}

export interface AccountRecord {
  account_id: number;
  account_type: string;
  balance: string;
  cash_balance: string;
  is_taxable: boolean;
  model: AccountModelInfo;
  model_source: "household" | "account" | "residual" | "none";
  processing_order: number;

  // Optional: the console's fixtures predate the key, and the api sends
  // `null` for a payload written before it existed.
  cash_balance_override?: CashBalanceOverrideData | null;
  cash_adjustment: CashAdjustmentData | null;
  restriction_adjustment: RestrictionAdjustmentData | null;
  secondary_securities: SecondarySecuritiesData | null;
  tax_constraints: TaxConstraintsData | null;
  constrained_allocation: ConstrainedAllocationData | null;
  trade_generation: TradeGenerationData | null;

  checkpoints: Partial<Record<CheckpointKey, CheckpointData>>;

  trade: AccountTrade;
  achieved_risk_weights: Record<string, string>;
  target_risk_weights: Record<string, string>;
  within_threshold: boolean;
}

export interface FinalTrade {
  buy_total: string;
  sell_total: string;
  trade_count: number;
  shares_net: string;
  // Optional household-level realized gain rollups; populated by the
  // python pipeline once `v3_final_from_native` derives them from
  // `account_outcomes[].trades[]` (see APP-1241-final-rollup-gaps.md
  // in the python repo).
  realized_gain_st_total?: string;
  realized_gain_lt_total?: string;
}

export interface WashSaleRow {
  account_id: number;
  symbol: string;
  avoided_wash_sale: boolean;
  impact: string;
}

export interface FinalData {
  balance: string;
  trade: FinalTrade;
  target_risk_weights: Record<string, string>;
  achieved_risk_weights: Record<string, string>;
  risk_deltas: Record<string, string>;
  asset_class_deltas: Record<string, string>;
  within_risk_threshold: boolean;
  within_asset_class_threshold: boolean;
  shortfall_reasons: string[];
  limiting_constraints: string[];
  wash_sale_considerations: WashSaleRow[];
}

export interface ExplainResponseV3 {
  rebalance_id: number;
  generated_at: string;
  schema_version: 3;
  trigger: TriggerData;
  request: RequestData;
  model_assignment: ModelAssignmentData;
  accounts: AccountRecord[];
  final: FinalData;
}

// Canonical 7-key set; UI iterates this when rendering the summary
// matrix. Mirror python `CHECKPOINT_KEYS`.
export const CHECKPOINT_COLUMNS: CheckpointKey[] = [
  "holdings",
  "model",
  "cash",
  "restrictions",
  "substitutions",
  "constrain",
  "final",
];
